import {Injectable} from '@angular/core';
import {BaseService} from './BaseService';
import {APIServiceConnector} from './APIServiceConnector';
import * as enumModels from '../Models/EnumModels';
import {EntityCodes, ErrorCodes, ModuleLicenceCodes} from '../Models/EnumModels';
import {ModuleMultilingualLabels, UIState, UserState} from '../Models/UIModels';
import {AppSettings} from '../Library/AppSettings';
import {
    CallCategoryLookup,
    CallDescriptionLookup,
    GenericMaster,
    GlitchActionReason,
    GlitchCategory,
    GlitchDescription,
    GlitchRecoveryOptionMaster,
    GlobalMasters,
    LanguageLookup,
    LocationLookup,
    MNTMasters,
    PropertyAppFeatures,
    PropertyBasicInfo,
    PropertyMainMasters,
    PropertyMainMastersHelper, PropertyZoneUrl,
    ReasonLookup,
    SectionsLookup,
    SkillLookup,
    StaffBasicInfo
} from '../Models/GlobalEntities';
import * as Collections from 'typescript-collections';
import {cloneDeep, find, groupBy, mapValues, some} from "lodash";
import {DeviceDetectorService} from 'ngx-device-detector';
import {CookieService} from 'ngx-cookie-service';
import {isObject} from 'util';
import {DateTime, FixedOffsetZone} from 'luxon';
import {SearchTextResult} from "../Models/CommonModels";

@Injectable()
export class MasterDataService extends BaseService {

    public LoggedInUserData: StaffBasicInfo;
    public CurrentUIState: UIState;
    public CurrentUserState: UserState;
    public UILabels: Array<ModuleMultilingualLabels>;
    public Cache_PropertyMainMasters = new Collections.Dictionary<number, PropertyMainMasters>();
    public Cache_Global: GlobalMasters;
    public Cache_MNT : MNTMasters;
    public BaseImageURL: string = AppSettings.URL_Users;
    public ActivePropertyID: number = 0;
    public TransferModel: any = null;
    ReturnURL: string = "";
    ReturnURLModel: string = "";

    private ZoneUrls: Array<PropertyZoneUrl> = [];

    constructor(public apiService: APIServiceConnector) {
        super();

        this.BaseURL = AppSettings.URL_Masters;

        this.CurrentUIState = new UIState();
        this.CurrentUIState.SelectedModule = enumModels.Modules.NoService;
        this.CurrentUIState.SreenName = null;
        this.CurrentUserState = new UserState();
    }

    RefreshToken() {
        if (!AppSettings.EnableUnifocusSSO) {
            return Promise.resolve()
        }
        return this.apiService
            .postAPI(AppSettings.URL_Session, "SSO/Staff_EnsureSession", {DeviceType: 'SSO_WEB'}).then(data => {
                console.log(data)
                if (!data || data.HasError || !data.Result) {
                    return;
                }
                const sessionId = data.Result.SessionId;
                this.apiService.setCookie("SessionId", sessionId);
            });
    }

    GetStaticUILabelsForWeb(PropertyID: number, LanguageCode: string): Promise<any> {
        return this.apiService.getAPI(this.BaseURL, "API/GetStaticUILabelsForWeb?PropertyID=" + this.URLEncode(PropertyID) + "&LanguageCode=" + this.URLEncode(LanguageCode)).then(data => {
            this.UILabels = data;
            return this.UILabels;
        });
    }

    SetCurrentLoggedInUser(): Promise<any> {
        return this.apiService.getAPI(AppSettings.URL_Users, "Staff/Staff_BasicInfo_Self").then(data => {
            if (data && !data.HasError) {
                this.LoggedInUserData = data;
                this.LoggedInUserData.AppFeatures = isObject(data.AppFeatures) && mapValues(data.AppFeatures, this.TransformAppFeatures);
                this.ActivePropertyID = data.PropertyId;
                return data;
            } else {
                if (data.Errors.findIndex(a => a.ErrorCode == ErrorCodes.InvalidUserToken) >= 0) {
                    alert("Invalid Session or Session expired. Please login again");
                    AppSettings.EnableUnifocusSSO ? this.apiService.signOutUser() : window.location.assign(AppSettings.URL_Central);
                }
            }

        });
    }

    TransformAppFeatures(data): PropertyAppFeatures {
        const parseAsInt = (numLike) => isNaN(+numLike) ? null : +numLike;
        const transformed = new PropertyAppFeatures();
        if (data) {
            transformed.AdvanceSecurity = data.AdvanceSecurity === "1";
            transformed.WebAuthExpiry = parseAsInt(data.WebAuthExpiry);

            transformed.PreArrivalDaysLimit = parseAsInt(data.PreArrivalDaysLimit);
            transformed.CheckOutDaysLimit = parseAsInt(data.CheckOutDaysLimit);

            transformed.MINIBAR_VOID_ALLOWED = data.MINIBAR_VOID_ALLOWED === "1";
            transformed.MINIBAR_VOID_DURATION_SECS = parseAsInt(data.MINIBAR_VOID_DURATION_SECS);

            transformed.MinibarHopTime = parseAsInt(data.MinibarHopTime);
            transformed.MinibarSameRoomRepostTime = parseAsInt(data.MinibarSameRoomRepostTime);

            transformed.MinibarShowRepostAlert = data.MinibarShowRepostAlert === "TRUE";
            transformed.MinibarShowSeekBar = data.MinibarShowSeekBar === "TRUE";

            transformed.MinibarTotalSeekTime = parseAsInt(data.MinibarTotalSeekTime);
        }
        return transformed;
    }



    GetAllPropertyMaster(PropertyID: number): Promise<PropertyMainMasters> {
        return this.apiService.getAPI(this.BaseURL, "API/GetAllPropertyMaster?PropertyID=" + this.URLEncode(PropertyID)).then(data => {
            let trans: PropertyMainMastersHelper = new PropertyMainMastersHelper();

            for (let i = 0; i < data.length; i++) {
                let row = data[i];
                this.Cache_PropertyMainMasters.setValue(row.PropertyID, row);
            }

            trans.TransformToLookup(this.Cache_PropertyMainMasters);
            return data;
        });
    }

    GetMNTMaster(): Promise<any> {
        let lang = "";
        if(this.Cache_PropertyMainMasters.getValue(this.ActivePropertyID) && this.Cache_PropertyMainMasters.getValue(this.ActivePropertyID).LanguageCode){
            lang = this.Cache_PropertyMainMasters.getValue(this.ActivePropertyID).LanguageCode
        }
        return this.apiService.getAPI(AppSettings.URL_KMTMasters, "KMTMaster/GetKMTMasters?lang=" + lang).then(data => {
            if(data && data.Result){
                this.Cache_MNT = data.Result
            }
        });

    }

    GetDescription(propertyId: number, id : number){
        let callDescriptionName = this.FindCallDescription(propertyId, id);
        if(callDescriptionName && callDescriptionName.Description) return callDescriptionName.Description;
        else {
            if(this.Cache_MNT && this.Cache_MNT.Descriptions){
                let MNTCallDescriptionName =  this.Cache_MNT.Descriptions.find((des) => des.Id === id);
                return (MNTCallDescriptionName && MNTCallDescriptionName.WorkOrderDescription) ? MNTCallDescriptionName.WorkOrderDescription : "";
            }
            else return "";
        }
    }

    GetReasonTypes(PropertyId: number, ReasonType: string): ReasonLookup[] {
        return this.GetPropertyCache(PropertyId).Reason.filter(val => val.ReasonType == ReasonType);
    }

    GetPropertyCache(PropertyId: number): PropertyMainMasters {
        return this.Cache_PropertyMainMasters.getValue(+PropertyId);
    }

    getInventoryDetails(PropertyId: number){
        return this.GetPropertyCache(PropertyId).InventoryCallDescriptionMaster;
    }

    getInventoryDetailsById(PropertyId: number, InventoryId: number){
      const allInventory = this.GetPropertyCache(PropertyId).InventoryCallDescriptionMaster;
       return allInventory.find(elem => elem.InventoryItemId == InventoryId) || null;
    }

    getInventoryDetailByPropertyDescription(PropertyId: number, DescriptionId: number){
      const inventory =  this.GetPropertyCache(PropertyId).InventoryCallDescriptionMaster;
        return inventory.find(elem => elem.CallDescriptionId == DescriptionId) || null;
    }

    GetVIPDetails(PropertyId: number): any[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;
        return obj.VIPs;
    }

    GetCallDescritpion(PropertyId: number): CallDescriptionLookup[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.CallDescriptions;
    }

    GetCallCategories(PropertyId: number): CallCategoryLookup[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.CallCategories;
    }

    GetDepartments(PropertyId: number): GenericMaster[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.Dept;
    }

    GetEventMaster(PropertyId: number): GenericMaster[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.EventMaster;
    }

    GetStaff(PropertyId: number): GenericMaster[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.Staff;
    }

    GetGroups(PropertyId: number): GenericMaster[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.Groups;
    }

    GetLocations(PropertyId: number): LocationLookup[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.Location;
    }

    GetLanguage(PropertyId: number): LanguageLookup[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.Language;
    }

    GetSections(PropertyId: number): SectionsLookup[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.Sections;
    }

    GetSkills(PropertyId: number): SkillLookup[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.Skill;
    }

    GetGlitchDescritpion(PropertyId: number): GlitchDescription[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.GlitchDescriptionMaster;
    }

    GetGlitchCategories(PropertyId: number): GlitchCategory[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.GlitchCategories;
    }

    GetGlitchActionReasons(PropertyId: number): GlitchActionReason[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.GlitchActionReason;
    }

    GetGlitchRecoveryOptions(PropertyId: number): GlitchRecoveryOptionMaster[] {
        let obj = this.GetPropertyCache(PropertyId);
        if (obj == null)
            return null;

        return obj.GlitchRecoveryOptionMaster;
    }

    GetGlitchTemperaments(): GenericMaster[] {
        return this.Cache_Global.GuestTemperamentMaster;
    }

    GetAllDeviceType(PropertyId: number): GenericMaster[] {
         let obj = this.GetPropertyCache(PropertyId);
         if (obj == null)
              return null;

        return obj.DeviceType;
    }

    GetAllGlobalMasters(): Promise<GlobalMasters> {
        return this.apiService.getAPI(this.BaseURL, "API/GetAllStaticMasters").then(data => {
            this.Cache_Global = data;
            return data;
        });
    }

    InitializeStartupData(): Promise<any> {
        return Promise.all([
            this.SetCurrentLoggedInUser(),
            this.SetZoneUrls(),
            this.GetAllPropertyMaster(null),
            this.GetAllGlobalMasters()
        ]);
    }

    InitializeMNTStartupData(): Promise<void> {
        return this.CheckActiveLicence_Module(this.ActivePropertyID, 'KMT_CORE') ?
            this.GetMNTMaster() : Promise.resolve();
    }
    SetReturnURL(ReturnURL: string, ReturnURLModel: any): void {
        this.ReturnURL = ReturnURL;
        if (ReturnURLModel != null)
            this.ReturnURLModel = JSON.stringify(ReturnURLModel);
        else
            this.ReturnURLModel = null;
    }

    ResetReturnURL(): void {
        this.ReturnURL = null;
        //this.ReturnURLModel = null;
    }

    GetLoginPropertyId(): number {
        if (this.LoggedInUserData == null)
            return 0;

        return this.LoggedInUserData.PropertyId;
    }

    GetPropertyIDs(): Array<number> {
        let ret: Array<number> = [];

        if (this.LoggedInUserData == null || this.LoggedInUserData.Properties == null)
            return ret;

        for (let i = 0; i < this.LoggedInUserData.Properties.length; i++) {
            ret.push(this.LoggedInUserData.Properties[i].PropertyId);
        }

        return ret;
    }

    GetProperty(PropertyId: number): PropertyBasicInfo {
        if (this.LoggedInUserData == null || this.LoggedInUserData.Properties == null)
            return null;

        for (let i = 0; i < this.LoggedInUserData.Properties.length; i++) {
            let row = this.LoggedInUserData.Properties[i];
            if (row.PropertyId == PropertyId)
                return row;
        }

        return null;
    }

    GetPropertyDateFormat(PropertyId: number): string {
        if (this.LoggedInUserData == null)
            return 'MM/DD/MM';

        if (PropertyId == null) {
            PropertyId = this.LoggedInUserData.PropertyId;
        }

        let row = this.GetProperty(PropertyId);
        if (row == null)
            return 'MM/DD/MM';

        return row.PropertyDateFormat.toUpperCase() + " " + row.PropertyTimeFormat;
    }

    MasterData_GetMutli(PropertyID: number, ID: any, EntityCode: enumModels.EntityCodes, CurrentValue: any): any {
        if (ID == null)
            return "";

        let obj = new PropertyMainMastersHelper();

        return obj.GetMasterValue(this.Cache_PropertyMainMasters, PropertyID, ID, EntityCode, CurrentValue);
    }

    FindInGenericMaster(arr: Array<GenericMaster>, ID: number): GenericMaster {
        if (arr == null)
            return null;

        for (let i = 0; i < arr.length; i++) {
            if (arr[i].Id == ID)
                return arr[i];
        }

        return null;
    }

    StaticData_GetMutli(ID: any, EntityCode: enumModels.EntityCodes, CurrentValue: any): any {
        var lst = this.Cache_Global[EntityCode];

        let row = this.FindInGenericMaster(lst, ID);
        if (row == null)
            return CurrentValue;

        return row.Name;
    }

    FindLocation(PropertyId: number, ID: number): LocationLookup {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.Location.length; i++) {
            let row = obj.Location[i];
            if (row.Id == ID)
                return row;
        }

        return null;
    }

    FindSkill(PropertyId: number, ID: number): SkillLookup {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.Skill.length; i++) {
            let row = obj.Skill[i];
            if (row.Id == ID) {
                return row;
            }
        }

        return null;
    }

    FindSections(PropertyId: number, DeptId: number): number[] {
        let obj = this.GetPropertyCache(PropertyId);
        let secArr : Array<number> = [];
        for (let i = 0; i < obj.Sections.length; i++) {
            let row = obj.Sections[i];
            if (row.DeptId == DeptId) {
                secArr.push(obj.Sections[i].Id);
            }
        }
        if (secArr.length > 0) {
            return secArr;
        }

        return null;
    }

    GetCallDescription_Dept(PropertyId: number, ID: number): number {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.CallDescriptions.length; i++) {
            let row = obj.CallDescriptions[i];
            if (row.Id == ID) {
                let cat = this.FindCallCategory(PropertyId, row.CategoryId);
                if (cat == null)
                    return;

                let sk = this.FindSkill(PropertyId, cat.SkillId);
                if (sk == null)
                    return null;

                return sk.DeptId;
            }

        }

        return null;
    }

    FindCallDescription(PropertyId: number, CallDescriptionId: number): CallDescriptionLookup {
        if (!CallDescriptionId || !PropertyId) {
            return null;
        }

        return find(this.GetCallDescritpion(PropertyId) || [], {
            Id: CallDescriptionId
        });
    }

    FindCallCategory(PropertyId: number, ID: number): CallCategoryLookup {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.CallCategories.length; i++) {
            let row = obj.CallCategories[i];
            if (row.Id == ID)
                return row;
        }

        return null;
    }

    FindStaff(PropertyId: number, ID: number): GenericMaster {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.Staff.length; i++) {
            let row = obj.Staff[i];
            if (row.Id == ID)
                return row;
        }

        return null;
    }

    FindEventMaster(PropertyId: number, ID: number): GenericMaster {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.EventMaster.length; i++) {
            let row = obj.EventMaster[i];
            if (row.Id == ID)
                return row;
        }

        return null;
    }

    FindReason(PropertyId: number, ID: number): ReasonLookup {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.Reason.length; i++) {
            let row = obj.Reason[i];
            if (row.ID == +ID)
                return row;
        }

        return null;
    }

    FindDepartment(PropertyId: number, ID: number): GenericMaster {
        let obj = this.GetPropertyCache(PropertyId);
        for (let i = 0; i < obj.Dept.length; i++) {
            let row = obj.Dept[i];
            if (row.Id == +ID)
                return row;
        }

        return null;
    }

    ChangeScreenLanguage(lang): Promise<any> {
        return this.apiService.getAPI(AppSettings.URL_Session, "Session/ChangeLanguage?LanguageCode=" + lang).then(data => {
            return data;
        })
    }
    InitializeClientConfig(): Promise<any> {
        return Promise.all([this.SetConfig()]);
    }

    SetConfig(): Promise<any> {
        return this.apiService.getAPI("/", "Settings").then((data) => {
            data = data || {};
            AppSettings.URL_Masters = data.URL_Masters;
            AppSettings.URL_ComplainService = data.URL_ComplainService;
            AppSettings.URL_KMTConfig = data.URL_KMTConfig;
            AppSettings.URL_Messaging = data.URL_Messaging;
            AppSettings.URL_PMSService = data.URL_PMSService;
            AppSettings.URL_Session = data.URL_Session;
            AppSettings.URL_Users = data.URL_Users;
            AppSettings.URL_Central = data.URL_Central;
            AppSettings.URL_Analytics_Glitch = data.URL_Analytics_Glitch;
            AppSettings.URL_Analytics_HK = data.URL_Analytics_HK;
            AppSettings.URL_Analytics_Service = data.URL_Analytics_Service;
            AppSettings.URL_AdvancedReports = data.URL_AdvancedReports;
            AppSettings.URL_OldHTML = data.URL_OldHTML;
            AppSettings.URL_KMTMasters = data.URL_KMTMasters;
            AppSettings.URL_WorkOrders = data.URL_WorkOrders;
            AppSettings.URL_Config = data.URL_Config;
            AppSettings.Domain = data.Domain;
            AppSettings.URL_GlitchService = data.URL_GlitchService;
            AppSettings.URL_HK = data.URL_HK;
            AppSettings.URL_Inspection = data.URL_Inspection;
            AppSettings.Domain = data.Domain;
            AppSettings.URL_KMT = data.URL_KMT;
            AppSettings.URL_PlatformConfigHTML = data.URL_PlatformConfigHTML;
            this.BaseURL = AppSettings.URL_Masters;
            AppSettings.ShowICPNumber = data.ShowICPNumber;
            AppSettings.ShowHTMLConfig = data.ShowHTMLConfig;
            AppSettings.URL_Inventory = data.URL_Inventory;
            AppSettings.URL_Attachments = data.URL_Attachments;
            AppSettings.APP_DEBUG = data.APP_DEBUG;
            AppSettings.ShowSilverlightConfig = data.ShowSilverlightConfig;
            AppSettings.ShowAdvanceReportDirectly = (data.ShowAdvanceReportDirectly || '').toLowerCase() == 'true';
            AppSettings.ShowGlitchVisitorSearch = (data.ShowGlitchVisitorSearch || '').toLowerCase() == 'true';
        })
    }

    SetZoneUrls() {
        if (!AppSettings.EnableUnifocusSSO) {
            this.ZoneUrls = [];
            return Promise.resolve();
        } else {
            return this.apiService.getAPI("/", "Product/Zone_GetUrls")
                .then(response => {
                    if (!response || response.HasError || !response.Result) {
                        this.ZoneUrls = [];
                        return;
                    }

                    const result = response.Result || [];
                    this.ZoneUrls = result
                        .filter(obj => obj.PropertyId && obj.PropertyCode)
                        .map(PropertyZoneUrl.FromResponse);
                }, (error) => {
                    console.error(error);
                    this.ZoneUrls = [];
                });
        }
    }

    GetDefaultProperty_Action(Action: string): number {
        let lstProp = this.GetProperties_Action(Action);

        if (lstProp.length == 0)
            return null;

        if (lstProp.findIndex(a => a.PropertyId == this.ActivePropertyID) >= 0)
            return this.ActivePropertyID;
        else
            return lstProp[0].PropertyId;
    }

    GetProperties_Action(Action: string): Array<PropertyBasicInfo> {
        let lstProps = cloneDeep((this.LoggedInUserData && this.LoggedInUserData.Properties) || []);
        let lstAuth = (this.LoggedInUserData && this.LoggedInUserData.PropertyAuthorizations) || [];
        let lstLicence = (this.LoggedInUserData && this.LoggedInUserData.ActiveLicences) || [];
        let auth = lstAuth.find(a => a.ActionCode == Action);
        let module;
        if (auth)
            module = auth.ModuleCode;
        lstProps = lstProps.filter(p => {
            return lstLicence.findIndex(a => a.PropertyId == p.PropertyId && a.RoleModule == module) >= 0;
        })

        return lstProps.filter(p => {
            return (lstAuth.findIndex(a => a.IsAllowed == true && a.ActionCode == Action && a.PropertyId == p.PropertyId) >= 0);
        });

    }

    GetProperties_ModuleLicenseCode(moduleCode: ModuleLicenceCodes): PropertyBasicInfo[] {
        const properties = cloneDeep((this.LoggedInUserData && this.LoggedInUserData.Properties) || []);
        const moduleLicenses = (this.LoggedInUserData && this.LoggedInUserData.ActiveLicences) || [];
        return properties.filter(property => find(
            moduleLicenses,
            {PropertyId: property.PropertyId, MODULE_CODE: moduleCode}));
    }

    GetDefaultProperty_ModuleLicenseCode(moduleCode: ModuleLicenceCodes): number | null {
        const lstProp = this.GetProperties_ModuleLicenseCode(moduleCode);
        if (!lstProp || !lstProp.length) {
            return null;
        }
        const defaultProperty = find(lstProp, {PropertyId: this.ActivePropertyID});
        const [head] = lstProp;
        return defaultProperty
            ? defaultProperty.PropertyId
            : head.PropertyId;
    }

    GetProperties_ActionCombo(Action1: string, Action2: string): Array<PropertyBasicInfo> {
        let lstProps = cloneDeep((this.LoggedInUserData && this.LoggedInUserData.Properties) || []);
        let lstAuth = (this.LoggedInUserData && this.LoggedInUserData.PropertyAuthorizations) || [];

        let auth = lstAuth.find(a => a.ActionCode == Action1);
        let module;
        if (auth) {
            module = auth.ModuleCode;
        }
        let lstLicence = (this.LoggedInUserData && this.LoggedInUserData.ActiveLicences) || [];

        lstProps = lstProps.filter(p => {
            return (lstLicence.findIndex(a => a.PropertyId == p.PropertyId && a.RoleModule == module) >= 0);
        })

        return lstProps.filter(p => {
            return ((lstAuth.findIndex(a => a.IsAllowed == true && a.ActionCode == Action1 && a.PropertyId == p.PropertyId) >= 0) || (lstAuth.findIndex(a => a.IsAllowed == true && a.ActionCode == Action2 && a.PropertyId == p.PropertyId) >= 0));
        });
    }

    GetDepartments_Action(PropertyId:number, Action: string): Array<GenericMaster> {

        if (this.CheckActiveLicence_Action(PropertyId, Action)) {
            let lst = cloneDeep(this.GetDepartments(PropertyId));
            let lstAuth = (this.LoggedInUserData && this.LoggedInUserData.PropertyAuthorizations) || [];

            return lst.filter(p => {
                return (lstAuth.findIndex(a => a.IsAllowed == true && a.ActionCode == Action && a.PropertyId == PropertyId && a.DeptId == p.Id) >= 0);
            });
        }
        return [];

    }

    CheckAuth_Action(Action: string): boolean {
        let lstAuth = (this.LoggedInUserData && this.LoggedInUserData.PropertyAuthorizations) || [];
        let userRight = lstAuth.find(a => a.ActionCode == Action);
        if (userRight && this.GetProperties_ModuleLicence(null, userRight.ModuleCode).length > 0) {
            if (lstAuth.findIndex(a => a.IsAllowed == true && a.ActionCode == Action) >= 0)
                return true;
        }
        return false;
    }

    CheckAuth_Dept_Action(PropertyId: number, DeptId: number, Action: string): boolean {
        if (this.CheckActiveLicence_Action(PropertyId, Action)) {
            let lstAuth = (this.LoggedInUserData && this.LoggedInUserData.PropertyAuthorizations) || [];
            if (lstAuth.findIndex(a => a.IsAllowed == true && a.DeptId == DeptId && a.ActionCode == Action && a.PropertyId == PropertyId) >= 0)
                return true;
            else
                return false;
        }
        return false;

    }

    CheckActiveLicence_Action(PropertyId: number, Action: string) {
        let userRight = ((this.LoggedInUserData && this.LoggedInUserData.PropertyAuthorizations) || []).find(a => a.ActionCode == Action);
        if (userRight && ((this.LoggedInUserData && this.LoggedInUserData.ActiveLicences) || []).findIndex(a => a.PropertyId == PropertyId && a.RoleModule == userRight.ModuleCode) >= 0) {
            return true;
        }
        return false;
    }

    GetProperties_ModuleLicence(moduleCode: string, roleModule?: string) {
        let lstLicence = (this.LoggedInUserData && this.LoggedInUserData.ActiveLicences) || [];
        let lstProps = cloneDeep((this.LoggedInUserData && this.LoggedInUserData.Properties) || []);
        if (!lstLicence.length || !lstProps.length) {
            return [];
        }
        let module = moduleCode;
        if (moduleCode == null) {
            const match = lstLicence.find(a => a.RoleModule == roleModule);
            if (!match) {
                return [];
            }
            module = match.MODULE_CODE;
        }
        return lstProps.filter(p => {
            return (lstLicence.findIndex(a => a.PropertyId == p.PropertyId && a.MODULE_CODE == enumModels.ModuleLicenceCodes.TritonCore) >= 0) && (lstLicence.findIndex(a => a.PropertyId == p.PropertyId && a.MODULE_CODE == module) >= 0);
        })
    }
    ChangeProperty(prop): Promise<any> {
        return this.apiService.getAPI(AppSettings.URL_Session, "Session/ChangeProperty?PropertyID=" + prop).then(data => {
            return data;
        })
    }

    CheckActiveLicence_Module(PropertyId: number, Module: string) {
        const licences = this.LoggedInUserData && this.LoggedInUserData.ActiveLicences;
        if (!licences) {
            return false;
        }
        return licences.findIndex(a => a.PropertyId == PropertyId && a.MODULE_CODE == Module) >= 0;
    }

    GetGlitchDescriptionForCall(CallDescriptionId: number, PropertyId: number): Promise<number> {
        return this.apiService.getAPI(this.BaseURL, "API/GetGlitchDescriptionForCall?CallDescriptionId=" + this.URLEncode(CallDescriptionId) + "&PropertyId=" + this.URLEncode(PropertyId), true).then(data => {
            return data;
        })
    }

    CheckWebAuthorizationRight(PropertyId:number, Action: string){
        let lstAuth = ((this.LoggedInUserData && this.LoggedInUserData.PropertyAuthorizations) || []).filter(x => x.PropertyId == PropertyId);
        let userRight = lstAuth.find(a => a.ActionCode == Action);
        if (userRight && this.GetProperties_ModuleLicence(null, userRight.ModuleCode).length > 0) {
            if (lstAuth.findIndex(a => a.IsAllowed == true && a.ActionCode == Action) >= 0)
                return true;
        }
        return false;
    }

    GetComplainActionLabel(action: string): string {
        let label = 'LBL.UNKNOWN';
        switch (action) {
            case "REG": label = 'LBL.LBL_REGISTERED'; break;
            case "OPN": label = 'LBL.LBL_OPN_DET'; break;
            case "PRK": label = 'LBL.LBL_PARKED'; break;
            case "ROP": label = 'LBL.LBL_REOPENED'; break;
            case "USN": label = 'LBL.LBL_TXT_UNS'; break;
            case "SNL": label = 'LBL.LBL_SNL_DET'; break;
            case "REAS": label = 'LBL.LBL_REASSIGNED'; break;
            case "CLS": label = 'LBL.LBL_CLOSE_DET'; break;
            case "ADD": label = 'LBL.LBL_ATTACHMENT_ADDED'; break;
            case "REM": label = 'LBL.LBL_REMARKS_ADDED'; break;
            case "ESCL": label = 'LBL.LBL_ESCALATED'; break;
            case "TAG": label = 'LBL.TAGGED_STAFF'; break;
            case "DEL": label = 'LBL.LBL_ATTACHMENT_DELETED'; break;
            case "RST": label = 'LBL.LBL_RESET'; break;
            case "MSG": label = 'LBL.LBL_MSG'; break;
        }
        return label;
    }

    GetPropertyAppFeatures(propertyId: number): PropertyAppFeatures | null {
        if (!propertyId) {
            return null;
        }
        const appFeatures = this.LoggedInUserData
            && this.LoggedInUserData.AppFeatures
            && this.LoggedInUserData.AppFeatures[`${propertyId}`];

        if (!appFeatures) {
            return null;
        }
        return appFeatures;
    }

    GetPropertyOffsetInSecs(propertyId: number): number {
        const property = this.GetProperty(propertyId);
        return (property && property.BaseUtcOffset) || 0;
    }

    GetPropertyTime(propertyId: number): DateTime {

        const offset = this.GetPropertyOffsetInSecs(propertyId);
        const zone = FixedOffsetZone.instance(offset / 60);
        const local = DateTime.local();
        const rezoned = local.setZone(zone);
        return rezoned;
    }

    CheckAnyActiveLicense_Module(moduleCode: ModuleLicenceCodes) {
        return some((this.LoggedInUserData && this.LoggedInUserData.ActiveLicences) || [], {MODULE_CODE: moduleCode});
    }

    GetTypeaheadMasters(propertyIds: number[], entityCodes: EntityCodes[]) {
        const result = {} as Record<EntityCodes, Array<any>>;
        if (!propertyIds || !propertyIds.length || !entityCodes || !entityCodes.length) {
            return result;
        }
        for (const propertyId of propertyIds) {
            const objProp = this.Cache_PropertyMainMasters.getValue(propertyId);
            if (!objProp) {
                break;
            }
            for (const entityCode of entityCodes) {
                const existing = result[entityCode] || [];
                let cache = [];
                switch (entityCode) {
                    case EntityCodes.Location:
                        cache = objProp.Location || [];
                        break;
                    case EntityCodes.CallDescriptions:
                        cache = objProp.CallDescriptions || [];
                        break;
                    case EntityCodes.CallCategories:
                        cache = objProp.CallCategories || [];
                        break;
                    case EntityCodes.Staff:
                        cache = objProp.Staff || [];
                        break;
                    case EntityCodes.Dept:
                        cache = objProp.Dept || [];
                        break;
                    case EntityCodes.GlitchDescriptionMaster:
                        cache = objProp.GlitchDescriptionMaster || [];
                        break;
                    case EntityCodes.GlitchCategories:
                        cache = objProp.GlitchCategories || [];
                        break;
                }
                const indexed = groupBy(existing, 'Id');

                for (const cacheElement of cache) {
                    if (!indexed[cacheElement.Id]) {
                        existing.push(cacheElement);
                    }
                }
                result[entityCode] = existing;
            }
        }
        return result;
    }

    SearchTypeaheadMasters(cache: Record<EntityCodes, Array<any>>, term?: string) {
        if (term === null || term === undefined || !term.length) {
            return;
        }
        term = term.trim().toLowerCase();
        const result = {} as Record<EntityCodes, Array<any>>;
        for (const entityCode in cache) {
            let key = '';
            const rows: Array<any> = cache[entityCode];
            if (!rows) {
                break;
            }
            switch (entityCode) {
                case EntityCodes.CallCategories:
                case EntityCodes.CallDescriptions:
                case EntityCodes.GlitchCategories:
                case EntityCodes.GlitchDescriptionMaster:
                case EntityCodes.Location:
                    key = 'Description';
                    break;
                case EntityCodes.Staff:
                case EntityCodes.Dept:
                case EntityCodes.Sections:
                    key = 'Name';
                    break;
            }
            if (!key) {
                break;
            }
            const filtered = rows.filter(
                row => (row[key] || '')
                    .toLowerCase()
                    .indexOf(term) >= 0);

            if (filtered.length) {
                result[entityCode] = filtered
            }
        }
        return result;
    }

    GetSearchResultsForTypeahead(cache: Record<EntityCodes, Array<any>>, term?: string) {
        const filtered = this.SearchTypeaheadMasters(cache, term);
        const entities = Object.keys(filtered || {});
        if (!entities.length) {
            return [];
        }
        return entities.map(entityCode => {
            const result = new SearchTextResult();
            result.Entity = EntityCodes[entityCode];
            result.EntityName = entityCode;
            result.Term = term;
            result.ItemCount = (filtered[entityCode] || []).length;

            return result;
        })
    }

    GetZoneUrls(propertyId: number) {
        const [filtered] = (this.ZoneUrls || []).filter(obj => obj.PropertyId === propertyId)
        return filtered;
    }
}
